import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAppToast from '@/hooks/useAppToast';
import { deleteClinic } from '@/services/admin/clinics';

type DeleteClinicVariables = { siteType: string; id: number };
type DeleteClinicOptions = {
  onDeleted?: () => void;
  /** Called when API returns 500 (e.g. clinic not found or already deleted). Caller can show 500 UI on same page without changing URL. */
  onServerError?: () => void;
};

/**
 * React Query mutation hook for deleting a clinic (site admin).
 * Success handling (toast, invalidate, optional redirect) is done in onSuccess.
 */
export const useDeleteClinicMutation = (options?: DeleteClinicOptions) => {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: ({ siteType, id }: DeleteClinicVariables) => deleteClinic(siteType, id),
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data.message || '削除しました。');
        queryClient.invalidateQueries({ queryKey: ['site-admin', 'clinics'] });
        options?.onDeleted?.();
      } else {
        toast.error(data?.message || '削除できませんでした。');
      }
    },
    onError: (error: unknown) => {
      const status = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;
      if (status === 500) {
        options?.onServerError?.();
      } else {
        toast.error('削除できませんでした。');
      }
    },
  });
};
